import { BusinessGetResponseType } from "@sellar-io/api-v2/types";
import { ExtendedFirebaseUser } from "../../../../hooks/useFirebaseUser.shared";
import { fetchProducts } from "./fetchProducts";
import { CheckoutOrderItem } from "./types";
import { useState } from "react";
import { isPromotionalCheckoutOrderItem } from "../../categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { fetchPromotionalProduct } from "./fetchPromotionalProduct";

type ProductWithIssue = {
  id: number;
  name: string;
  description: string;
};
export type OutOfStockProduct = ProductWithIssue;
export type ActiveProduct = { id: number };
export interface PricingChangeProduct extends ProductWithIssue {
  oldPrice: number;
  newPrice: number;
}

export type ValidateCheckoutParams = {
  orderItemList: CheckoutOrderItem[];
  business: BusinessGetResponseType;
  user: ExtendedFirebaseUser | undefined;
  supplierId: number;
  storefrontLinkPassword: string | undefined;
};

export const useValidateCheckout = () => {
  const [outOfStockProducts, setOutOfStockProducts] = useState<
    OutOfStockProduct[]
  >([]);
  const [pricingChangeProducts, setPricingChangeProducts] = useState<
    PricingChangeProduct[]
  >([]);
  const [activeProducts, setActiveProducts] = useState<ActiveProduct[]>([]);

  const [loading, setLoading] = useState(false);

  const handleValidateCheckout = ({
    orderItemList,
    business,
    user,
    supplierId,
    storefrontLinkPassword,
  }: ValidateCheckoutParams): Promise<{
    outOfStockProducts: OutOfStockProduct[];
    pricingChangeProducts: PricingChangeProduct[];
    activeProducts: ActiveProduct[];
  }> => {
    setLoading(true);
    setOutOfStockProducts([]);
    setPricingChangeProducts([]);
    setActiveProducts([]);

    return Promise.all([
      handleValidateNonPromotionalOrderItems({
        user,
        business,
        supplierId,
        storefrontLinkPassword,
        orderItemList,
      }),
      handleValidatePromotionalOrderItems({
        user,
        business,
        orderItemList,
      }),
    ])
      .then(
        ([
          validateNonPromotionalItemsResponse,
          validatePromotionalItemsResponse,
        ]) => {
          const updatedOutOfStockProducts = [
            ...validateNonPromotionalItemsResponse.outOfStockProducts,
            ...validatePromotionalItemsResponse.outOfStockProducts,
          ];
          setOutOfStockProducts(updatedOutOfStockProducts);

          const updatedPricingChangeProducts = [
            ...validateNonPromotionalItemsResponse.pricingChangeProducts,
          ];

          setPricingChangeProducts(updatedPricingChangeProducts);

          const updatedActiveProducts = [
            ...validateNonPromotionalItemsResponse.activeProducts,
            ...validatePromotionalItemsResponse.activeProducts,
          ];
          setActiveProducts(updatedActiveProducts);

          return {
            outOfStockProducts: updatedOutOfStockProducts,
            pricingChangeProducts: updatedPricingChangeProducts,
            activeProducts: updatedActiveProducts,
          };
        },
      )
      .finally(() => {
        setLoading(false);
      });
  };

  const isOutOfStock = (
    product: Pick<
      CheckoutOrderItem,
      "availableStock" | "canBackOrder" | "quantity"
    >,
  ) => {
    if (product.availableStock < product.quantity && !product.canBackOrder) {
      return true;
    }

    if (product.availableStock <= 0 && !product.canBackOrder) {
      return true;
    }

    return false;
  };

  const handleValidateNonPromotionalOrderItems = async ({
    orderItemList,
    user,
    business,
    supplierId,
    storefrontLinkPassword,
  }: Omit<ValidateCheckoutParams, "retailerId">): Promise<{
    outOfStockProducts: OutOfStockProduct[];
    pricingChangeProducts: PricingChangeProduct[];
    activeProducts: ActiveProduct[];
  }> => {
    const nonPromotionalCheckoutOrderItems = orderItemList.filter(
      (orderItem) => !isPromotionalCheckoutOrderItem(orderItem),
    );

    if (nonPromotionalCheckoutOrderItems.length === 0) {
      return {
        outOfStockProducts: [],
        pricingChangeProducts: [],
        activeProducts: [],
      };
    }

    const orderItemsLookupMap = new Map(
      nonPromotionalCheckoutOrderItems.map((orderItem) => [
        orderItem.productId,
        orderItem,
      ]),
    );

    return fetchProducts({
      user,
      business,
      params: {
        SupplierId: supplierId,
        id: nonPromotionalCheckoutOrderItems.map(
          (orderItem) => orderItem.productId,
        ),
        storefrontLinkPassword,
      },
    })
      .then((productData) => {
        const outOfStockProducts = productData
          .filter((product) =>
            isOutOfStock({
              canBackOrder: product.canBackorder,
              availableStock: product.availableStock,
              quantity: orderItemsLookupMap.get(product.id)?.quantity ?? 0,
            }),
          )
          .map((product) => {
            const nameAndDescription = product.name?.split("•") ?? [];
            const name = nameAndDescription[0] ?? "";
            const description = nameAndDescription[1] ?? "";

            return {
              name,
              description,
              id: product.id,
            };
          });

        const activeProducts = productData.map((product) => {
          return {
            id: product.id,
          };
        });

        const pricingChangeProducts = productData
          .map((product) => {
            const nameAndDescription = product.name?.split("•") ?? [];
            const name = nameAndDescription[0] ?? "";
            const description = nameAndDescription[1] ?? "";
            const orderItem = orderItemsLookupMap.get(product.id);
            const oldPrice = orderItem?.price ?? 0;
            const newPrice = product.ProductPriceTier?.price ?? 0;
            return {
              name,
              description,
              id: product.id,
              oldPrice,
              newPrice,
            };
          })
          .filter((product) => product.oldPrice !== product.newPrice);

        return {
          outOfStockProducts,
          pricingChangeProducts,
          activeProducts,
        };
      })
      .catch(() => {
        return {
          outOfStockProducts: [],
          pricingChangeProducts: [],
          activeProducts: [],
        };
      });
  };

  const handleValidatePromotionalOrderItems = async ({
    orderItemList,
    user,
    business,
  }: Pick<
    ValidateCheckoutParams,
    "orderItemList" | "user" | "business"
  >): Promise<{
    outOfStockProducts: OutOfStockProduct[];
    activeProducts: ActiveProduct[];
  }> => {
    const promotionalCheckoutOrderItems = orderItemList.filter((orderItem) =>
      isPromotionalCheckoutOrderItem(orderItem),
    );

    if (promotionalCheckoutOrderItems.length === 0) {
      return {
        outOfStockProducts: [],
        activeProducts: [],
      };
    }

    const orderItemsLookupMap = new Map(
      promotionalCheckoutOrderItems.map((orderItem) => [
        orderItem.productId,
        orderItem,
      ]),
    );

    return Promise.all(
      promotionalCheckoutOrderItems.map((checkoutOrderItem) => {
        return fetchPromotionalProduct({
          user,
          business,
          promotionRewardItemId: checkoutOrderItem.promotionRewardItemId ?? 0,
        });
      }),
    )
      .then((response) => {
        const productData = response.map((response) => response.Product);
        const outOfStockProducts = productData
          .filter((product) =>
            isOutOfStock({
              canBackOrder: product.canBackorder,
              availableStock: product.availableStock,
              quantity: orderItemsLookupMap.get(product.id)?.quantity ?? 0,
            }),
          )
          .map((product) => {
            const nameAndDescription =
              (product.name ?? product.nameV2)?.split("•") ?? [];
            const name = nameAndDescription[0] ?? "";
            const description = nameAndDescription[1] ?? "";

            return {
              name,
              description,
              id: product.id,
            };
          });

        // Promotional items can be active or inactive - both are valid
        const activeProducts = productData.map((product) => {
          return {
            id: product.id,
          };
        });

        return {
          outOfStockProducts,
          activeProducts,
        };
      })
      .catch(() => {
        return {
          outOfStockProducts: [],
          activeProducts: [],
        };
      });
  };

  return {
    handleValidateCheckout,
    loading,
    outOfStockProducts,
    pricingChangeProducts,
    activeProducts,
  };
};
